import i18next from 'i18next';

import { changeAppDateTimeLocale } from '$app-web/utils/app-date-time/utils';

import languages from './languages';

const FALLBACK_LANGUAGE = 'en-US';

/**
 * Load the given namespace's translation bundle into i18next for `languageCode` (plus the
 * en-US fallback), mark it as the default namespace, and switch the active language and
 * date-time locale.
 *
 * This is the resource-loading core of the i18n saga's `setI18nextLanguage`, extracted so it
 * can be reused outside the saga pipeline — notably by the standalone chat iframe, whose space
 * deliberately bypasses the saga's space handshake and therefore never loads namespace
 * resources on its own.
 *
 * `languageCode` must already be a supported code (one `languages.get` accepts); callers are
 * responsible for normalizing/validating it first, exactly as `checkAndSetLanguage` does.
 */
export async function loadNamespaceTranslations(languageCode: string, namespace: string): Promise<void> {
    const targetLanguage = languages.get(languageCode);
    const fallbackLanguage = languages.get(FALLBACK_LANGUAGE);

    if (!i18next.hasResourceBundle(languageCode, namespace)) {
        const namespaceTranslations = await targetLanguage.getNamespaceTranslations(namespace);
        i18next.addResources(languageCode, namespace, namespaceTranslations);
    }

    // Always keep en-US loaded as the fallback for any keys missing in the active language.
    if (languageCode !== FALLBACK_LANGUAGE && !i18next.hasResourceBundle(FALLBACK_LANGUAGE, namespace)) {
        const fallbackTranslations = await fallbackLanguage.getNamespaceTranslations(namespace);
        i18next.addResources(FALLBACK_LANGUAGE, namespace, fallbackTranslations);
    }

    i18next.setDefaultNamespace(namespace);
    changeAppDateTimeLocale(languageCode);
    await i18next.changeLanguage(languageCode);
}
